import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase } from './supabase'
import { AccountStatus } from '@/types/account'

interface AccountContextType {
  status: AccountStatus | null
  loading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

const AccountContext = createContext<AccountContextType | undefined>(undefined)

export function AccountProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AccountStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  
  const fetchAccountStatus = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        console.log('[AccountProvider] No session found')
        throw new Error('No session found')
      }
      
      // console.log('[AccountProvider] Fetching account status with session:', session.user.id)
      
      const response = await fetch(
        `${import.meta.env.VITE_WORKERS_URL}/rainfactory/account/status`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        }
      )
      
      if (!response.ok) {
        throw new Error('Failed to fetch account status')
      }
      
      const data = await response.json()
      // console.log('[AccountProvider] Account status response:', data)
      // console.log('[AccountProvider] Personal account:', data.personalAccount)
      // console.log('[AccountProvider] Personal account ID:', data.personalAccount?.id)
      
      setStatus(data)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'))
      console.error('Error fetching account status:', err)
    } finally {
      setLoading(false)
    }
  }, [])
  
  useEffect(() => {
    fetchAccountStatus()
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // Only refetch on TOKEN_REFRESHED, not SIGNED_IN
      // SIGNED_IN events are fired when tabs regain focus, causing unnecessary refetches
      if (event === 'TOKEN_REFRESHED') {
        fetchAccountStatus()
      }
    })
    
    return () => subscription.unsubscribe()
  }, [fetchAccountStatus])
  
  return (
    <AccountContext.Provider value={{ status, loading, error, refresh: fetchAccountStatus }}>
      {children}
    </AccountContext.Provider>
  )
}

export function useAccountStatus() {
  const context = useContext(AccountContext)
  if (context === undefined) {
    throw new Error('useAccountStatus must be used within an AccountProvider')
  }
  return context
}